import { attachListingsAndPrices, attachLockedItems, attachObjects, extractKioskData, getAllDynamicFields, getKioskObject } from "../utils.mjs";
import { KIOSK_OWNER_CAP } from "../types/kiosk.mjs";
import { Extension, ExtensionKey } from "../contracts/0x2/kiosk_extension.mjs";
import { getAllObjects } from "./client-utils.mjs";
import { PersonalKioskCap } from "../contracts/kiosk/personal_kiosk.mjs";
import { isValidSafeAddress } from "@safecoin/safe/utils";
//#region src/query/kiosk.ts
async function fetchKiosk(client, kioskId, pagination, options) {
	const data = await getAllDynamicFields(client, kioskId, pagination);
	const listings = [];
	const lockedItemIds = [];
	const kioskData = extractKioskData(data, listings, lockedItemIds, kioskId);
	const [kiosk, listingObjects, items] = await Promise.all([
		options.withKioskFields ? getKioskObject(client, kioskId) : Promise.resolve(void 0),
		options.withListingPrices ? getAllObjects(client, kioskData.listingIds) : Promise.resolve([]),
		options.withObjects ? getAllObjects(client, kioskData.itemIds) : Promise.resolve([])
	]);
	if (options.withKioskFields) kioskData.kiosk = kiosk;
	attachListingsAndPrices(kioskData, listings, listingObjects);
	attachLockedItems(kioskData, lockedItemIds);
	attachObjects(kioskData, items);
	return {
		data: kioskData,
		nextCursor: null,
		hasNextPage: false
	};
}
/**
* A function to fetch all the user's kiosk Caps
* And a list of the kiosk address ids.
* Returns a list of `kioskOwnerCapIds` and `kioskIds`.
* Extra options allow pagination.
*/
const DEFAULT_PAGE_SIZE = 50;
const PERSON_KIOSK_CURSOR = "personal";
const OWNED_KIOSKS_CURSOR = "owned";
async function getOwnedKiosks(client, address, options) {
	if (!isValidSafeAddress(address)) return {
		nextCursor: null,
		hasNextPage: false,
		kioskOwnerCaps: [],
		kioskIds: []
	};
	const limit = options?.pagination?.limit ?? DEFAULT_PAGE_SIZE;
	const [cursorType, cursor] = options?.pagination?.cursor?.split(":") ?? [PERSON_KIOSK_CURSOR, null];
	if (options?.personalKioskType && cursorType === PERSON_KIOSK_CURSOR) {
		const personalResult = await client.core.listOwnedObjects({
			owner: address,
			type: options.personalKioskType,
			cursor,
			limit,
			include: { content: true }
		});
		const personalKioskResponse = formatOwnedKioskResponse({
			data: personalResult.objects.filter((obj) => !(obj instanceof Error)).map((obj) => {
				return {
					obj,
					kioskId: PersonalKioskCap.parse(obj.content).cap?.for
				};
			}).filter((item) => item.kioskId !== void 0),
			hasNextPage: personalResult.hasNextPage,
			nextCursor: personalResult.cursor
		}, PERSON_KIOSK_CURSOR);
		if (personalKioskResponse.hasNextPage) return personalKioskResponse;
		const remainingLimit = limit - personalKioskResponse.kioskOwnerCaps.length;
		if (remainingLimit < 1) return {
			nextCursor: `${OWNED_KIOSKS_CURSOR}:`,
			hasNextPage: true,
			kioskOwnerCaps: personalKioskResponse.kioskOwnerCaps,
			kioskIds: personalKioskResponse.kioskIds
		};
		const ownedResult = await client.core.listOwnedObjects({
			owner: address,
			type: KIOSK_OWNER_CAP,
			cursor: null,
			limit: remainingLimit,
			include: { content: true }
		});
		const { KioskOwnerCap: KioskOwnerCapParser } = await import("../contracts/0x2/kiosk.mjs");
		const ownedKiosksResponse = formatOwnedKioskResponse({
			data: ownedResult.objects.filter((obj) => !(obj instanceof Error)).map((obj) => {
				return {
					obj,
					kioskId: KioskOwnerCapParser.parse(obj.content).for
				};
			}).filter((item) => item.kioskId !== void 0),
			hasNextPage: ownedResult.hasNextPage,
			nextCursor: ownedResult.cursor
		}, OWNED_KIOSKS_CURSOR);
		return {
			nextCursor: ownedKiosksResponse.nextCursor,
			hasNextPage: ownedKiosksResponse.hasNextPage,
			kioskOwnerCaps: [...personalKioskResponse.kioskOwnerCaps, ...ownedKiosksResponse.kioskOwnerCaps],
			kioskIds: [...personalKioskResponse.kioskIds, ...ownedKiosksResponse.kioskIds]
		};
	}
	const result = await client.core.listOwnedObjects({
		owner: address,
		type: KIOSK_OWNER_CAP,
		cursor: cursor || null,
		limit,
		include: { content: true }
	});
	const { KioskOwnerCap: KioskOwnerCapParser } = await import("../contracts/0x2/kiosk.mjs");
	return formatOwnedKioskResponse({
		data: result.objects.filter((obj) => !(obj instanceof Error)).map((obj) => {
			return {
				obj,
				kioskId: KioskOwnerCapParser.parse(obj.content).for
			};
		}).filter((item) => item.kioskId !== void 0),
		hasNextPage: result.hasNextPage,
		nextCursor: result.cursor
	}, OWNED_KIOSKS_CURSOR);
}
function formatOwnedKioskResponse(response, cursorType) {
	const { data, hasNextPage, nextCursor } = response;
	return {
		nextCursor: nextCursor ? `${cursorType}:${nextCursor}` : nextCursor,
		hasNextPage,
		kioskOwnerCaps: data.map((item) => {
			return {
				isPersonal: item.obj.type?.includes("PersonalKioskCap") || false,
				digest: item.obj.digest,
				version: item.obj.version,
				objectId: item.obj.objectId,
				kioskId: item.kioskId
			};
		}),
		kioskIds: data.map((item) => item.kioskId)
	};
}
async function fetchKioskExtension(client, kioskId, extensionType) {
	const { dynamicField } = await client.core.getDynamicField({
		parentId: kioskId,
		name: {
			type: `0x2::kiosk_extension::ExtensionKey<${extensionType}>`,
			bcs: ExtensionKey.serialize({ dummy_field: false }).toBytes()
		}
	});
	const extension = Extension.parse(dynamicField.value.bcs);
	return {
		objectId: dynamicField.fieldId,
		type: extensionType,
		isEnabled: extension.is_enabled,
		permissions: extension.permissions.toString(),
		storageId: extension.storage.id,
		storageSize: Number(extension.storage.size)
	};
}
//#endregion
export { fetchKiosk, fetchKioskExtension, getOwnedKiosks };

//# sourceMappingURL=kiosk.mjs.map